import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, Phone, Mail, HelpCircle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { useLanguage } from '../context/LanguageContext';

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export const FAQ = () => {
  const { t } = useLanguage();

  return (
    <div className="overflow-hidden pt-20" data-testid="faq-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div {...fadeUp} viewport={{ once: true }} className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#D4AF37] text-sm font-medium uppercase tracking-widest mb-4 block">
              {t.faq.subtitle}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              {t.faq.title1}{' '}
              <span className="text-[#D4AF37]">{t.faq.title2}</span>
            </h1>
            <p className="text-white/60 text-base md:text-lg leading-relaxed">
              {t.faq.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="pb-24 md:pb-32">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          {t.faq.categories.map((category, categoryIndex) => (
            <motion.div
              key={categoryIndex}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
              className="mb-12"
              data-testid={`faq-category-${categoryIndex}`}
            >
              <h2 className="text-xl md:text-2xl font-semibold text-white mb-6 flex items-center gap-3">
                <HelpCircle className="w-6 h-6 text-[#D4AF37]" />
                {category.title}
              </h2>
              
              <Accordion type="single" collapsible className="space-y-3">
                {category.questions.map((item, index) => (
                  <AccordionItem
                    key={index}
                    value={`${categoryIndex}-${index}`}
                    className="bg-[#0A0A0A] border border-white/10 rounded-xl overflow-hidden hover:border-[#D4AF37]/30 transition-colors data-[state=open]:border-[#D4AF37]/50"
                  >
                    <AccordionTrigger className="px-6 py-4 text-left text-white hover:text-[#D4AF37] hover:no-underline [&[data-state=open]]:text-[#D4AF37]">
                      <span className="text-sm md:text-base font-medium pr-4">{item.q}</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-4 text-white/60 text-sm leading-relaxed">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div
            {...fadeUp}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
              {t.faq.contactTitle}
            </h2>
            <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto mb-8">
              {t.faq.contactDesc}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a
                href="tel:+4915126777707"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-8 py-4 rounded-full font-semibold hover:bg-[#E5C34A] transition-all duration-300 gold-glow"
                data-testid="faq-phone-btn"
              >
                <Phone className="w-5 h-5" />
                +49 151 26777707
              </a>
              <a
                href="mailto:service@parkingdus.com"
                className="inline-flex items-center justify-center gap-2 border border-white/20 text-white px-8 py-4 rounded-full font-medium hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                data-testid="faq-email-btn"
              >
                <Mail className="w-5 h-5" />
                {t.faq.emailBtn}
              </a>
            </div>

            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 text-[#D4AF37] font-medium hover:underline"
            >
              {t.faq.contactFormLink}
              <ChevronRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Book CTA */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div
            {...fadeUp}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden"
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1556388158-158ea5ccacbd?q=80&w=2070&auto=format&fit=crop')`,
              }}
            />
            <div className="absolute inset-0 bg-black/70" />

            <div className="relative z-10 px-8 py-16 md:px-16 md:py-24 text-center">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
                {t.faq.bookCtaTitle}
              </h2>
              <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto mb-8">
                {t.faq.bookCtaDesc}
              </p>
              <a
                href="https://mcdgroup.de/buchungssystem/index.php"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-10 py-5 rounded-full font-semibold text-lg hover:bg-[#E5C34A] transition-all duration-300 gold-glow"
                data-testid="faq-book-btn"
              >
                {t.faq.bookCtaBtn}
                <ChevronRight className="w-5 h-5" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
